/**
 * Копирование протокола так, чтобы вставка читалась.
 *
 * Протокол хранится в Markdown, и раньше в буфер уходил он сам: в Word, Outlook или
 * Лензе вставлялись «**», «##» и «*» вместо оформления (Антон Долгов, 08.10.2026:
 * «не читаемый или плохо читаемый файл»). Теперь в буфер кладутся две формы сразу:
 * HTML — для редакторов, которые понимают оформление, и простой текст без разметки —
 * для мессенджеров и блокнота. Какую взять, решает программа, в которую вставляют.
 */

const BLOCK = new Set(['P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'UL', 'OL', 'TABLE', 'PRE', 'BLOCKQUOTE', 'HR', 'DIV']);

function inlineText(node) {
  let out = '';
  node.childNodes.forEach((ch) => {
    if (ch.nodeType === 3) out += ch.nodeValue;
    else if (ch.nodeName === 'BR') out += '\n';
    else if (ch.nodeName === 'INPUT' && ch.type === 'checkbox') out += ch.checked ? '☑ ' : '☐ ';
    else if (ch.nodeName === 'IMG') out += ch.getAttribute('alt') || '';
    else if (ch.nodeName === 'A') {
      // Адрес ссылки в простом тексте не терять: в мессенджере от «документ» без него толку нет.
      const text = inlineText(ch); const href = ch.getAttribute('href') || '';
      out += href && href !== text ? `${text} (${href})` : text;
    } else if (!BLOCK.has(ch.nodeName)) out += inlineText(ch);
  });
  return out.replace(/[ \t]+\n/g, '\n');
}

const indent = (text, pad) => text.split('\n').map((l) => pad + l).join('\n');

function listToPlain(list, depth) {
  const lines = [];
  const pad = '    '.repeat(depth);
  const first = Number(list.getAttribute('start'));
  let n = Number.isFinite(first) && list.hasAttribute('start') ? first : 1;
  Array.from(list.children).forEach((li) => {
    if (li.nodeName !== 'LI') return;
    const marker = list.nodeName === 'OL' ? `${n++}.` : '•';
    const own = [];
    const nested = [];
    li.childNodes.forEach((ch) => {
      if (ch.nodeName === 'UL' || ch.nodeName === 'OL') nested.push(listToPlain(ch, depth + 1));
      // Пункт «свободного» списка оборачивает текст в <p> — берём его целиком.
      else if (ch.nodeName === 'P') own.push(inlineText(ch));
      // Код, цитата, таблица внутри пункта — отдельными строками под ним.
      else if (BLOCK.has(ch.nodeName)) nested.push(indent(nodesToPlain([ch]), pad + '    '));
      else own.push(ch.nodeType === 3 ? ch.nodeValue : inlineText({ childNodes: [ch] }));
    });
    lines.push(`${pad}${marker} ${own.join(' ').replace(/\s+/g, ' ').trim()}`);
    lines.push(...nested.filter(Boolean));
  });
  return lines.join('\n');
}

function nodesToPlain(nodes) {
  const blocks = [];
  Array.from(nodes).forEach((el) => {
    if (el.nodeType === 3) {
      if (el.nodeValue.trim()) blocks.push(el.nodeValue.trim());
    } else if (el.nodeName === 'UL' || el.nodeName === 'OL') {
      blocks.push(listToPlain(el, 0));
    } else if (el.nodeName === 'TABLE') {
      blocks.push(Array.from(el.querySelectorAll('tr'))
        .map((tr) => Array.from(tr.children).map((c) => inlineText(c).trim()).join(' | ')).join('\n'));
    } else if (el.nodeName === 'HR') {
      blocks.push('———');
    } else if (el.nodeName === 'PRE') {
      blocks.push(el.textContent.replace(/\n$/, ''));
    } else if (el.nodeName === 'BLOCKQUOTE' || el.nodeName === 'DIV') {
      const inner = nodesToPlain(el.childNodes);
      if (inner) blocks.push(el.nodeName === 'BLOCKQUOTE' ? indent(inner, '> ') : inner);
    } else {
      const t = inlineText(el).trim();
      if (t) blocks.push(t);
    }
  });
  return blocks.join('\n\n');
}

/** Отрисованный протокол (HTML) → простой текст: абзацы через пустую строку, пункты «•» и «1.». */
export function htmlToPlain(html) {
  // DOMParser, а не innerHTML: отсоединённый <div> с innerHTML всё равно начинает грузить
  // картинки и вызывает их onerror; документ из DOMParser ничего не загружает и не исполняет.
  return nodesToPlain(new DOMParser().parseFromString(html, 'text/html').body.childNodes);
}

/** Кладёт в буфер HTML и простой текст; где HTML в буфер нельзя — только текст. */
export async function copyRich(html, plain) {
  const clip = navigator.clipboard;
  if (!clip) throw new Error('Буфер обмена недоступен');
  if (typeof window.ClipboardItem === 'function' && typeof clip.write === 'function') {
    await clip.write([new window.ClipboardItem({
      'text/html': new Blob([html], { type: 'text/html' }),
      'text/plain': new Blob([plain], { type: 'text/plain' }),
    })]);
    return;
  }
  await clip.writeText(plain);
}
